import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from '../api/axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { ENDPOINT_ACCOUNT_RESET_PASSWORD, PASSWORD_REGEX } from '../Config';
import usePageTitle from '../hooks/usePageTitle';

function ResetPassword() {
    usePageTitle('Reset Password');
    const navigate = useNavigate();

    const [errMsg, setErrMsg] = useState('');
    const [success, setSuccess] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [pwd, setPwd] = useState('');
    const [validPwd, setValidPwd] = useState(false);
    const [pwdFocus, setPwdFocus] = useState(false);

    const [matchPwd, setMatchPwd] = useState('');
    const [validMatch, setValidMatch] = useState(false);
    const [matchFocus, setMatchFocus] = useState(false);

    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const [token, setToken] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            const response = await axios.put(ENDPOINT_ACCOUNT_RESET_PASSWORD,
                JSON.stringify({
                    token: token,
                    password: pwd
                }), {
                headers: {
                    'Content-Type': 'application/json'
                },
                withCredentials: true
            });
            if (response.status === 200) {
                setSuccess(true);
            }
        } catch (err) {
            if (!err?.response) {
                setErrMsg('No Server Response');
            } else if (err.response?.status === 400) {
                setErrMsg(err.response.data);
            } else {
                setErrMsg('Login Failed');
            }
        } finally {
            setSubmitting(false);
        }
    }

    useEffect(() => {
        const qsToken = searchParams.get('token');
        if (qsToken) {
            setToken(qsToken);
        } else {
            navigate('/forgot-password', { replace: true });
        }
    }, [searchParams]);

    useEffect(() => {
        setValidPwd(PASSWORD_REGEX.test(pwd));
        setValidMatch(pwd && pwd === matchPwd);
    }, [pwd, matchPwd]);

    return (
        <section>
            <div className="row d-flex justify-content-center">
                <div className="col-md-4">
                    <h1 className='mb-5'>Reset Password</h1>
                    <form onSubmit={handleSubmit}>
                        {success ?
                            <>
                                <p className='alert alert-success h6'>
                                    <div className='d-flex align-items-center'>
                                        <FontAwesomeIcon icon={faCheckCircle} size='3x' className='me-2'></FontAwesomeIcon>
                                        <span>
                                            Password has been successfully reset. <Link to="/login">Click here</Link> to sign in.
                                        </span>
                                    </div>
                                </p>
                            </>
                            :
                            <>
                                {errMsg &&
                                    <p className='alert alert-danger' aria-live="assertive">{errMsg}</p>
                                }
                                <div className="mb-3">
                                    <label htmlFor="password">
                                        Password:
                                    </label>
                                    <input
                                        className={`form-control ${validPwd ? "is-valid" : pwd ? "is-invalid" : ""}`}
                                        type="password"
                                        id="password"
                                        onChange={(e) => setPwd(e.target.value)}
                                        value={pwd}
                                        required
                                        aria-invalid={validPwd ? "false" : "true"}
                                        aria-describedby="pwdnote"
                                        onFocus={() => setPwdFocus(true)}
                                        onBlur={() => setPwdFocus(false)}
                                    />
                                    <small className="form-text text-muted">
                                        Minimum length: 8 characters. Recommended to include a mix of uppercase and lowercase letters, numbers, and special characters for better security.
                                    </small>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="confirm_pwd">
                                        Confirm Password:
                                    </label>
                                    <input
                                        className={`form-control ${validMatch ? "is-valid" : matchPwd ? "is-invalid" : ""}`}
                                        type="password"
                                        id="confirm_pwd"
                                        onChange={(e) => setMatchPwd(e.target.value)}
                                        value={matchPwd}
                                        required
                                        aria-invalid={validMatch ? "false" : "true"}
                                        aria-describedby="confirmnote"
                                        onFocus={() => setMatchFocus(true)}
                                        onBlur={() => setMatchFocus(false)}
                                    />
                                </div>
                                <div className="text-center">
                                    <button className="btn btn-primary" disabled={!validPwd || !validMatch || submitting}>
                                        {submitting && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>} Send
                                    </button>
                                </div>
                            </>
                        }
                    </form>
                </div>
            </div>

        </section>
    )
}

export default ResetPassword
